import { useEffect, useRef } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { create } from "zustand"

import type { CustomView } from "@productlane/zero/schema"

import type { useRoadmapFilterState } from "./roadmap-filter-state"

export type RoadmapViewFilters = NonNullable<CustomView["filters"]["roadmap"]>

type FilterStateSnapshot = Pick<
	ReturnType<typeof useRoadmapFilterState.getState>,
	| "negations"
	| "statuses"
	| "teamIds"
	| "projectIds"
	| "priorities"
	| "companyIds"
	| "sources"
	| "labelIds"
>

/** The roadmap filter state as a saved view stores it, empty lists left out. */
export function toRoadmapViewFilters(
	state: FilterStateSnapshot,
): RoadmapViewFilters {
	const filters: RoadmapViewFilters = {}
	const negations: NonNullable<RoadmapViewFilters["negations"]> = {}
	if (state.statuses.length > 0) {
		filters.statuses = state.statuses
		if (state.negations.statuses) negations.statuses = true
	}
	if (state.projectIds.length > 0) {
		filters.projectIds = state.projectIds
		if (state.negations.projectIds) negations.projectIds = true
	}
	if (state.teamIds.length > 0) filters.teamIds = state.teamIds
	if (state.priorities.length > 0) filters.priorities = state.priorities
	if (state.companyIds.length > 0) filters.companyIds = state.companyIds
	if (state.sources.length > 0) filters.sources = state.sources
	if (state.labelIds.length > 0) {
		filters.labelIds = state.labelIds
		if (state.negations.labelIds) negations.labelIds = true
	}
	if (Object.keys(negations).length > 0) filters.negations = negations
	return filters
}

/** The filter state a saved view opens with. */
export function fromRoadmapViewFilters(
	filters: RoadmapViewFilters | undefined,
): FilterStateSnapshot {
	return {
		negations: { ...filters?.negations },
		statuses: filters?.statuses ?? [],
		teamIds: filters?.teamIds ?? [],
		projectIds: filters?.projectIds ?? [],
		priorities: filters?.priorities ?? [],
		companyIds: filters?.companyIds ?? [],
		sources: filters?.sources ?? [],
		labelIds: filters?.labelIds ?? [],
	}
}

/** Whether the filters on screen still match what the view saved. */
export function sameRoadmapViewFilters(
	a: RoadmapViewFilters,
	b: RoadmapViewFilters,
): boolean {
	return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))
}

function normalize(filters: RoadmapViewFilters) {
	const sorted = (list?: Array<string | null>) =>
		list ? [...list].sort((x, y) => String(x).localeCompare(String(y))) : []
	return {
		statuses: sorted(filters.statuses),
		teamIds: sorted(filters.teamIds),
		projectIds: sorted(filters.projectIds),
		priorities: sorted(filters.priorities),
		companyIds: sorted(filters.companyIds),
		sources: sorted(filters.sources),
		labelIds: sorted(filters.labelIds),
		negatedLabels: !!filters.negations?.labelIds,
		negatedStatuses: !!filters.negations?.statuses,
		negatedProjects: !!filters.negations?.projectIds,
	}
}

interface ActiveRoadmapViewState {
	activeViewId: string | null
	// A just-saved view opens its name for editing in the nav.
	renamingViewId: string | null
	setActiveViewId: (id: string | null) => void
	setRenamingViewId: (id: string | null) => void
}

export const useActiveRoadmapView = create<ActiveRoadmapViewState>((set) => ({
	activeViewId: null,
	renamingViewId: null,
	setActiveViewId: (activeViewId) => set({ activeViewId }),
	setRenamingViewId: (renamingViewId) => set({ renamingViewId }),
}))

const VIEW_PARAM = "view"

/** Keeps `?view=<slug>` in step with the open view, so a view can be linked. */
export function useRoadmapViewUrlSync(
	views: readonly CustomView[],
	activeView: CustomView | undefined,
	openView: (view: CustomView) => void,
) {
	const viewParam = useSearch({ strict: false, select: (s) => s[VIEW_PARAM] })
	const navigate = useNavigate()
	const restored = useRef(false)

	useEffect(() => {
		if (restored.current || views.length === 0) return
		restored.current = true
		const view = viewParam ? views.find((v) => v.slug === viewParam) : undefined
		if (view) openView(view)
	}, [views, viewParam, openView])

	useEffect(() => {
		// Until the views load, a ?view= link has not been read yet.
		if (!restored.current) return
		const slug = activeView?.slug ?? null
		if ((viewParam ?? null) === slug) return
		void navigate({
			to: ".",
			search: (prev) => ({
				...prev,
				[VIEW_PARAM]: slug || undefined,
			}),
			replace: true,
		})
	}, [activeView?.slug, views.length, viewParam, navigate])
}
